import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import GeneralText from "components/atoms/text/GeneralText";
import StyledText from "components/atoms/text/StyledText";
import TitleText from "components/atoms/text/TitleText";

import useSpaceNavigate from "hooks/useSpaceNavigate";

const Container = styled.div`
    width: 100%;
`;

const TagItemWrap = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: auto;
    max-width: 180px;
    min-height: 20px;
`;

const TagItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: ${(props) => props.margin || "2px"};
    padding: 2px 4px;
    max-width: 180px;
    height: 20px;
    outline: none;
    background-color: var(--color-Tag);
    border: transparent;
    border-radius: 2px;
    cursor: pointer;
    transition: all 120ms ease-in;

    &:hover {
        background-color: var(--color-TagActive);
    }
`;

const MoreTag = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;
    padding: 2px 4px;
    max-width: 180px;
    height: 20px;
    outline: none;
    background-color: var(--color-TagActive);
    border: transparent;
    border-radius: 2px;
    cursor: pointer;
`;

const BlankBox = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 20px;
    background-color: var(--color-BaseBlue);
    border-radius: 2px;
`;

const BlankText = styled(StyledText)`
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const MoreBox = styled.div`
    position: fixed;
    top: ${({ position }) => position.top}px;
    right: ${({ position }) => position.right}px;
    display: ${({ active }) => (active ? "block" : "none")};
    width: 240px;
    max-width: 240px;
    min-height: 80px;
    background-color: var(--color-White);
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    z-index: 1;
`;

const MoreBoxHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 8px;
    width: 100%;
    height: 32px;
    border-bottom: solid 1px var(--color-Outline);
`;

const MoreContentWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 8px 8px 8px 8px;
    max-width: 240px;
    max-height: 160px;
    overflow-x: hidden;
    overflow-y: auto;
`;

function ReferenceTagList(props) {
    const { navigate } = useSpaceNavigate();
    const location = useLocation();
    const moreBoxRef = useRef();

    const [showTagBox, setShowTagBox] = useState(false);
    const [moreBoxPosition, setMoreBoxPosition] = useState({ top: 0, right: 0 });

    const handleMoreTagClick = (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setMoreBoxPosition({
            top: rect.top + rect.height + 8,
            right: window.innerWidth - rect.right,
        });
        setShowTagBox((prev) => !prev);
        e.stopPropagation();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (moreBoxRef.current && !moreBoxRef.current.contains(event.target)) {
                setShowTagBox(false);
            }
        };

        const handleScroll = () => {
            setShowTagBox(false);
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("scroll", handleScroll, true);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("scroll", handleScroll, true);
        };
    }, []);

    const selectedTags = location.state?.tags || [];

    const handleNavigate = (state) => {
        sessionStorage.setItem("activeTab", "Tags");
        navigate("/reference", { state });
    };

    return (
        <Container>
            {props.bibliography?.tag_mappings?.length > 0 ? (
                <TagItemWrap>
                    <Tooltip message={"필터 적용"}>
                        <TagItem
                            margin={"0 4px 0 0"}
                            style={
                                props.bibliography.tag_mappings.length > 1
                                    ? { maxWidth: "140px" }
                                    : { maxWidth: "180px" }
                            }
                            onClick={(e) => {
                                e.stopPropagation();
                                // 정확한 비교를 위해 숫자로 변환
                                const tagId = Number(props.bibliography.tag_mappings[0].id);
                                const selectedTagsToNumber = selectedTags.map(Number);

                                if (selectedTagsToNumber.includes(tagId)) {
                                    // tagId가 포함되어 있다면 해당 태그를 제거
                                    const newTags = selectedTagsToNumber.filter((tag) => tag !== tagId);
                                    handleNavigate({ tags: newTags });
                                } else {
                                    // 포함되어 있지 않다면 tagId를 추가
                                    handleNavigate({ tags: [...selectedTagsToNumber, tagId] });
                                }
                            }}
                        >
                            <GeneralText ellipsis size={"xsmall"}>
                                {props.bibliography.tag_mappings[0].name}
                            </GeneralText>
                        </TagItem>
                    </Tooltip>
                    {props.bibliography.tag_mappings.length > 1 && (
                        <MoreTag
                            onClick={(e) => {
                                handleMoreTagClick(e);
                            }}
                            ref={moreBoxRef}
                        >
                            <GeneralText ellipsis size={"xsmall"}>
                                +{props.bibliography.tag_mappings.length - 1}
                            </GeneralText>
                            <MoreBox active={showTagBox} position={moreBoxPosition}>
                                <MoreBoxHeader>
                                    <TitleText size={"subSmall"} color={"var(--color-SubBlack)"}>
                                        태그 목록
                                    </TitleText>
                                    <Icon
                                        name={"x"}
                                        size={"8"}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowTagBox(!showTagBox);
                                        }}
                                    />
                                </MoreBoxHeader>
                                <MoreContentWrap>
                                    {props.bibliography.tag_mappings.map((tag, index) => (
                                        <Tooltip message={"필터 적용"} key={index + "tag"}>
                                            <TagItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const tagId = Number(tag.id);
                                                    // selectedTags 배열의 값들을 모두 숫자로 변환
                                                    const selectedTagsNumbers = selectedTags.map(Number);

                                                    if (selectedTagsNumbers.includes(tagId)) {
                                                        const newTags = selectedTagsNumbers.filter(
                                                            (selectedTag) => selectedTag !== tagId,
                                                        );
                                                        handleNavigate({ tags: newTags });
                                                    } else {
                                                        handleNavigate({ tags: [...selectedTagsNumbers, tagId] });
                                                    }
                                                }}
                                            >
                                                <GeneralText ellipsis size={"xsmall"}>
                                                    {tag.name}
                                                </GeneralText>
                                            </TagItem>
                                        </Tooltip>
                                    ))}
                                </MoreContentWrap>
                            </MoreBox>
                        </MoreTag>
                    )}
                </TagItemWrap>
            ) : (
                <BlankBox>
                    <BlankText>연결된 태그가 없습니다.</BlankText>
                </BlankBox>
            )}
        </Container>
    );
}

export default ReferenceTagList;
